/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { css } from "@emotion/react";

interface RateCardProps {
  title: string;
  subtitle: string;
  percentage: number;
  change?: number;
  totalBars?: number;
}

const RateCard: React.FC<RateCardProps> = ({
  title,
  subtitle,
  percentage,
  change,
  totalBars = 30,
}) => {
  const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnimatedPercentage(clampedPercentage);
    }, 50); // slight delay to trigger animation
    return () => clearTimeout(timeout);
  }, [clampedPercentage]);

  const filledBars = Math.round((animatedPercentage / 100) * totalBars);

  // Determine bar color based on discrete ranges
  const getBarColor = (index: number) => {
    if (index >= filledBars) return "#d3d3d3"; // unfilled gray

    const barPercentage = ((index + 1) / totalBars) * 100;

    if (barPercentage <= 30) return "#ef4444"; // red
    if (barPercentage <= 70) return "#f59e0b"; // yellow
    return "#22c55e"; // green
  };

  return (
    <div
      css={css`
        width: 100%;
        padding: 24px;
        border-radius: 20px;
        background: #ffffff;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        border: 1px solid #f1f3f5;
        font-family: 'Inter', sans-serif;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        transition: transform 0.2s ease, box-shadow 0.2s ease;

        &:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
        }
      `}
    >
      {/* Header */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 4px;
        `}
      >
        <div css={css`display: flex; flex-direction: column; gap: 2px;`}>
          <span
            css={css`
                font-size: 15px;
                font-weight: 700;
                color: #1a1b1e;
              `}
          >
            {title}
          </span>
          <span
            css={css`
                color: #6b7280;
                font-size: 12px;
                font-weight: 500;
                `}
          >
            {subtitle}
          </span>
        </div>

        {change !== undefined && (
          <span
            css={css`
              background: #f0fdf4;
              color: #16a34a;
              font-weight: 600;
              font-size: 12px;
              padding: 2px 8px;
              border-radius: 4px;
              height: 20px;
            `}
          >
            {change >= 0 ? `+${change}%` : `${change}%`}
          </span>
        )}
      </div>

      {/* Progress bar container */}
      <div
        css={css`
          width: 100%;
          height: 60px;
          border: 1px solid #f1f3f5;
          border-radius: 12px;
          padding: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          overflow: hidden;
          margin-top: 30px;
          background: #fafafa;
        `}
      >
        {Array.from({ length: totalBars }).map((_, i) => (
          <div
            key={i}
            css={css`
              flex: 1;
              height: 100%;
              margin-right: ${i !== totalBars - 1 ? "3px" : "0"};
              border-radius: 2px;
              background: ${getBarColor(i)};
              transition: background 0.5s ease, transform 0.5s ease;
              transform: scaleY(${i < filledBars ? 1 : 0.8});
              opacity: ${i < filledBars ? 1 : 0.3};
              transform-origin: center;
            `}
          />
        ))}
      </div>

      {/* Percentage */}
      <div css={css`margin-top: auto; padding-top: 15px;`}>
        <span
          css={css`
              display: block;
              width: 100%;
              font-size: 19px;
              font-weight: 600;
              color: #111827;
              transition: color 0.5s ease;
              text-align: center;
            `}
        >
          {animatedPercentage}%
        </span>
      </div>
    </div>
  );
};

export default RateCard;
